import React, { useState } from "react";
import { Button } from "@chakra-ui/react";
import "../../Animations.css";

interface Base3DButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  small?: boolean;
  color?: "black" | "green" | "red" | "blue";
  isAnimating?: boolean;
  width?: string;
  responsiveWidth?: boolean;
  style?: React.CSSProperties;
}

const getBackgroundColor = (color: string) => {
  if (color === "black") return "";
  if (color === "red") return "var(--color-red)";
  if (color === "green") return "var(--color-green)";
  if (color === "blue") return "var(--color-blue)";
  return "";
};

const getShadowColor = (color: string) => {
  if (color === "black") return "var(--color-grey-light)";
  if (color === "red") return "#a0241e";
  if (color === "green") return "#2a8542";
  if (color === "blue") return "#0862a1";
  return "var(--color-grey-light)";
};

const Base3DButton: React.FC<Base3DButtonProps> = ({ 
  children,
  onClick,
  disabled = false,
  small = false,
  color = "black",
  isAnimating = false,
  width = "220px",
  responsiveWidth = false,
  style
}) => {
  const [temporarilyDisabled, setTemporarilyDisabled] = useState(false);

  const effectiveColor = isAnimating ? "black" : color;
  const shouldShake = effectiveColor === "red" && !disabled;

  const buttonWidth = small ? "auto" : (responsiveWidth ? "100%" : width);
  const buttonMaxWidth = small ? "auto" : width;
  const buttonMinWidth = small ? undefined : "100px";
  const buttonMinHeight = small ? undefined : "var(--button-height)";
  const buttonPadding = small ? 2 : undefined;
  const buttonWhiteSpace = small ? "nowrap" : "normal";
  const buttonWordWrap = small ? "normal" : "break-word";

  const handleClick = () => {
    if (disabled || temporarilyDisabled) return;
    setTemporarilyDisabled(true);
    if (onClick) onClick();
    setTimeout(() => setTemporarilyDisabled(false), 2000);
  };

  return (
    <div className={`relative inline-block m-1 ${shouldShake ? "button-shake" : ""}`}>
      <div 
        className="absolute top-1 w-full h-full rounded-xl z-0" 
        style={{ backgroundColor: getShadowColor(effectiveColor) }}
      />
      <Button
        onClick={handleClick}
        disabled={disabled || temporarilyDisabled}
        bg={getBackgroundColor(effectiveColor) || "white"}
        fontSize="var(--base-font-size)"
        rounded="xl"
        width={buttonWidth}
        maxWidth={buttonMaxWidth}
        minWidth={buttonMinWidth}
        height="auto"
        minHeight={buttonMinHeight}
        padding={buttonPadding}
        _hover={{}}
        _active={{}}
        _focus={{}}
        border="var(--color-grey-lightest)"
        className={`${isAnimating ? "button-press-animation" : ""}`}
        style={{
          position: "relative",
          zIndex: 1,
          boxShadow: "0px 2px 4px rgba(0,0,0,0.08)",
          color: effectiveColor !== "black" ? "white" : "inherit",
          whiteSpace: buttonWhiteSpace,
          wordWrap: buttonWordWrap,
          ...style
        }}
      >
        {children}
      </Button>
    </div>
  );
};

export default Base3DButton; 